import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const ThemeContext = createContext(null);
const API = import.meta.env.VITE_API_URL || '';

function getInitialTheme() {
  const stored = localStorage.getItem('weship-theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light';
}

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('weship-theme', theme);
}

/**
 * Apply custom CSS variables from theme_settings for the given mode.
 * Each setting's token maps to a CSS variable: token "primary" → "--primary".
 */
function applyCustomColors(settings, mode) {
  if (!settings || !settings.length) return;
  const root = document.documentElement;
  for (const s of settings) {
    if (s.mode !== mode && s.mode !== 'both') continue;
    const val = s.value || s.default_value;
    if (!val) continue;
    if (s.token === 'font-family') {
      applyFont(val);
    } else {
      root.style.setProperty(`--${s.token}`, val);
    }
  }
}

/** Load a Google Font dynamically and set the CSS variable */
function applyFont(fontName) {
  const root = document.documentElement;
  root.style.setProperty('--font-family', `'${fontName}', -apple-system, BlinkMacSystemFont, sans-serif`);

  const existing = document.getElementById('theme-font-link');
  const newHref = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`;
  if (existing && existing.href === newHref) return;
  if (existing) existing.href = newHref;
  else {
    const link = document.createElement('link');
    link.id = 'theme-font-link';
    link.rel = 'stylesheet';
    link.href = newHref;
    document.head.appendChild(link);
  }
}

/**
 * Apply theme variables from a theme preset (v2.308.0).
 * theme.variables = { light: { "--primary": "#338edc", ... }, dark: {...} }
 * Keys may include `--` prefix or not. `--font-family` triggers font loading.
 */
function applyThemeVariables(theme, mode) {
  if (!theme || !theme.variables) return;
  const root = document.documentElement;
  const vars = theme.variables[mode] || theme.variables.light || {};
  for (const [rawKey, val] of Object.entries(vars)) {
    if (val == null || val === '') continue;
    const key = rawKey.startsWith('--') ? rawKey : `--${rawKey}`;
    if (key === '--font-family') {
      applyFont(val);
    } else {
      root.style.setProperty(key, val);
    }
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [themeSettings, setThemeSettings] = useState([]);
  const [activeTheme, setActiveTheme] = useState(null); // v2.308.0
  const settingsLoaded = useRef(false);

  // Load theme settings from API once
  useEffect(() => {
    if (settingsLoaded.current) return;
    settingsLoaded.current = true;
    fetch(`${API}/api/theme-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.settings) {
          setThemeSettings(d.settings);
        }
      })
      .catch(() => {});
  }, []);

  // Apply theme + custom colors + active theme variables whenever theme/settings change
  useEffect(() => {
    applyTheme(theme);
    if (themeSettings.length > 0) {
      applyCustomColors(themeSettings, theme === 'dark' ? 'dark' : 'light');
    }
    if (activeTheme) {
      applyThemeVariables(activeTheme, theme);
    }
  }, [theme, themeSettings, activeTheme]);

  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // Sync theme (light/dark) to backend (fire-and-forget)
  const syncToBackend = useCallback((t) => {
    fetch(`${API}/api/auth/theme`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ theme: t }),
    }).catch(() => {});
  }, []);

  // v2.308.0 — Theme-Preset wechseln (theme_id)
  const setActiveThemeById = useCallback(async (themeId) => {
    try {
      const res = await fetch(`${API}/api/auth/theme`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ theme_id: themeId }),
      });
      if (!res.ok) return false;
      // Theme-Daten neu laden
      const meRes = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (meRes.ok) {
        const data = await meRes.json();
        if (data.user?.active_theme) {
          setActiveTheme(data.user.active_theme);
        }
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  // Load theme from backend on login
  const loadFromBackend = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.user?.theme && (data.user.theme === 'light' || data.user.theme === 'dark')) {
          setTheme(data.user.theme);
        }
        if (data.user?.active_theme) {
          setActiveTheme(data.user.active_theme);
        }
      }
    } catch { /* ignore */ }
  }, [setTheme]);

  // Reload theme settings from API (after save/reset in editor)
  const reloadThemeSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/theme-settings`);
      if (res.ok) {
        const d = await res.json();
        if (d?.settings) {
          setThemeSettings(d.settings);
        }
      }
    } catch { /* ignore */ }
  }, []);

  return (
    <ThemeContext.Provider value={{
      theme, setTheme, toggleTheme, syncToBackend, loadFromBackend,
      themeSettings, reloadThemeSettings,
      activeTheme, setActiveTheme, setActiveThemeById, // v2.308.0
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
